import { DragEvent, useEffect, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { SupplierDirectoryRow } from '../../../../types/advise';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { TextSpan } from '../../../Atoms/Text';
import { useEngagementEditor } from './EngagementEditorContext';
import { SubtitledDialogTitle } from './SubtitledDialogTitle';
import { CurrentSupplierNameField } from './CurrentSupplierNameField';
import { SupplierCatalogStatus } from './SupplierCatalogStatus';
import { InvoiceDropZone } from './InvoiceDropZone';
import { catalogMatch, newCurrentSupplierEntry } from './AddCurrentSupplierModal.calc';
import { CARD_ACTION_LINK } from './supplierCardStyles';
import { supplierKey } from './supplierKey';

export interface AddCurrentSupplierModalProps {
  onClose: () => void;
  // Null when the engagement changed while saving; manual entry is opened when asked for in place of an invoice.
  onAdded: (supplierId: string | null, openManualEntry: boolean) => void;
}

// A file dropped just outside the drop zone would otherwise open in the tab, losing what was typed.
const keepFileInPage = (event: DragEvent) => event.preventDefault();

/** Mount it only while open, so every opening starts empty. */
export const AddCurrentSupplierModal = ({ onClose, onAdded }: AddCurrentSupplierModalProps) => {
  const { engagement, currentSuppliers, supplierSelection } = useEngagementEditor();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<SupplierDirectoryRow | null>(null);
  const [invoice, setInvoice] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [linkDropped, setLinkDropped] = useState(false);
  // Catalog entries the API refused, such as one archived since the catalog loaded, which a typed name no longer links to.
  const [unlinked, setUnlinked] = useState<string[]>([]);
  const directory = supplierSelection?.directory;
  const load = supplierSelection?.load;
  const catalogLoading = !!supplierSelection && !directory && !supplierSelection.failed;
  // Until the catalog loads, a catalog name typed in would be saved without its link.
  const ready = name.trim() !== '' && !catalogLoading;
  const linkable = (directory ?? []).filter((row) => !unlinked.includes(supplierKey(row)));
  const linked = picked ?? catalogMatch(name, linkable);

  useEffect(() => {
    if (!directory) load?.();
  }, [directory, load]);

  const add = async (manualEntry: boolean) => {
    if (!currentSuppliers || !ready) return;
    setBusy(true);
    setRefusal(null);
    setLinkDropped(false);
    const added = await currentSuppliers.addSupplier(
      newCurrentSupplierEntry(name, linked, manualEntry),
      manualEntry ? null : invoice
    );
    setBusy(false);
    if (!added.refusal) return onAdded(added.supplierId, manualEntry);
    setRefusal(added.refusal);
    if (added.refusedAsSent && linked) unlink(linked);
  };

  const unlink = (row: SupplierDirectoryRow) => {
    setUnlinked((keys) => [...keys, supplierKey(row)]);
    setPicked(null);
    setLinkDropped(true);
    load?.();
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={busy ? undefined : onClose}>
      <SubtitledDialogTitle
        title="Add Current Supplier"
        subtitle={engagement.engagement_name}
        busy={busy}
        onClose={onClose}
      />
      <DialogContent
        dividers
        className="flex flex-col gap-4"
        onDragOver={keepFileInPage}
        onDrop={keepFileInPage}>
        {refusal && (
          <Alert severity="error">
            {refusal}
            {linkDropped && (
              <span className="block">
                Add again to save it without a catalog link, or pick it from the list once the catalog reloads.
              </span>
            )}
          </Alert>
        )}
        <CurrentSupplierNameField
          name={name}
          picked={picked}
          directory={directory ?? []}
          disabled={busy}
          onChange={(typed, row) => {
            setName(typed);
            setPicked(row);
          }}
        />
        {supplierSelection && (
          <SupplierCatalogStatus
            loading={catalogLoading}
            failed={!directory && supplierSelection.failed}
            onRetry={supplierSelection.load}
          />
        )}
        <InvoiceDropZone
          file={invoice}
          disabled={busy}
          onChange={setInvoice}
        />
        <TextSpan
          size="xs"
          color="grey6">
          No invoice?{' '}
          <button
            type="button"
            className={CARD_ACTION_LINK}
            disabled={!ready || busy}
            onClick={() => add(true)}>
            Enter spend manually instead
          </button>
        </TextSpan>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          disabled={busy}
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          disabled={!ready || busy}
          onClick={() => add(false)}>
          {busy ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Add Supplier'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
